import { useEffect, useState } from "react";
import { ConversationLoader } from "./OlliLogo";

// Design: components/PageLoader.tsx. The design fakes a ~1s load (usePageLoad);
// here pages pass their real loading state instead.

/** Olli's loader on the page itself, no card behind it. */
export function PageLoader({ label, className = "min-h-[62vh]" }) {
  return (
    <div role="status" aria-live="polite" className={`flex animate-fade-in flex-col items-center justify-center gap-4 ${className}`}>
      <ConversationLoader size={56} tone="white" />
      <p className="text-small font-medium text-muted">{label}</p>
    </div>
  );
}

/** A small Olli loader in place of a number or short value that has not arrived yet. */
export function InlineLoader({ size = 18, className = "" }) {
  return (
    <span className={`inline-flex align-middle ${className}`}>
      <ConversationLoader size={size} tone="white" />
    </span>
  );
}

/**
 * Loads data for a set of inputs (filters, page, id…) and says whether what is
 * on screen belongs to those inputs. `loading` is true until the answer for the
 * *current* `key` arrives, so a screen never shows the previous filter's rows
 * (or an empty state) under a new filter. Late answers for an old key are dropped.
 *
 *   const key = JSON.stringify({ type, page, search });
 *   const { data, loading, reload } = useKeyedLoad(key, () => apiGet(`/api/x?${q}`));
 *   {loading ? <PageLoader label="Loading x…" /> : <List rows={data.rows} />}
 *
 * `data` keeps the last answer (for the same or an older key) so counts and
 * totals can stay visible while a new page loads, if a screen wants that.
 */
export function useKeyedLoad(key, load, { enabled = true } = {}) {
  const [state, setState] = useState({ key: null, data: undefined });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    Promise.resolve()
      .then(load)
      .catch(() => null)
      .then((data) => { if (live) setState({ key, data }); });
    return () => { live = false; };
  }, [key, nonce, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    data: state.data,
    loading: enabled && state.key !== key,
    loadedKey: state.key,
    reload: () => setNonce((n) => n + 1),
    setData: (updater) => setState((s) => ({ ...s, data: typeof updater === "function" ? updater(s.data) : updater })),
  };
}
